import { useMemo, useState } from "react";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	Paper,
	Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ColGroupDef, CellClassParams } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import { formatProxyDate } from "@/shared/utils/proxyDateTime";
import moment from "moment";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";

const gridStyles = (
	<GlobalStyles
		styles={{
			".rm-shift-grid .group-border": {
				borderLeft: "1px solid #bdbdbd !important",
			},
			".rm-shift-grid .ag-header-row-column-group .ag-header-group-cell": {
				borderBottom: "1px solid #bdbdbd !important",
			},
			".rm-shift-grid [class*='ag-cell-error']": {
				color: "#d32f2f !important",
				fontWeight: 700,
			},
		}}
	/>
);

import { useRMShiftReport } from "../hooks/useRMShiftReport";
import { ToolbarControls } from "./ToolbarControls";
import type { RMShiftReportRow } from "../models/RMShiftReport";

const fmt2 = (val: number | null) => {
	if (val == null) return "";
	// Show integers without decimals (matches legacy display: "2", "825", "2000"
	// instead of "2.00", "825.00", "2000.00"). Real decimals keep 2 places.
	return Number.isInteger(val) ? val.toString() : val.toFixed(2);
};

const SECTION_BORDER = { borderLeft: "1px solid #bdbdbd" } as const;

const belowGoalClass = (
	params: CellClassParams<RMShiftReportRow>,
	valueField: keyof RMShiftReportRow,
	goalField: keyof RMShiftReportRow,
) => {
	const value = params.data?.[valueField] as number | null;
	const goal = params.data?.[goalField] as number | null;
	if (value != null && goal != null && value < goal) {
		return "ag-cell-error";
	}
	return "";
};

const buildColumnDefs = (): (
	| ColDef<RMShiftReportRow>
	| ColGroupDef<RMShiftReportRow>
)[] => [
	{
		headerName: "",
		children: [
			{
				field: "ProductionDate",
				headerName: "Prod Date",
				width: 100,
				sortable: true,
				valueFormatter: (p) => formatProxyDate(p.value),
			},
			{
				field: "ShiftId",
				headerName: "Shift",
				width: 60,
				sortable: true,
			},
			{
				field: "CrewId",
				headerName: "Crew",
				width: 60,
				sortable: true,
			},
			{
				field: "Size",
				headerName: "Size",
				minWidth: 180,
				flex: 1.5,
				sortable: true,
			},
		],
	},
	{
		headerName: "Tons",
		headerClass: "group-border",
		marryChildren: true,
		children: [
			{
				field: "TonsPerShiftGoal",
				headerName: "Goal",
				headerClass: "group-border",
				cellStyle: SECTION_BORDER,
				width: 75,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
			{
				field: "Tons",
				headerName: "Actual",
				width: 75,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
				cellClass: (p) => belowGoalClass(p, "Tons", "TonsPerShiftGoal"),
			},
		],
	},
	{
		headerName: "Time",
		headerClass: "group-border",
		marryChildren: true,
		children: [
			{
				field: "Hours",
				headerName: "Hours",
				headerClass: "group-border",
				cellStyle: SECTION_BORDER,
				width: 70,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
			{
				field: "HoursDown",
				headerName: "Down",
				width: 70,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
		],
	},
	{
		headerName: "Util %",
		headerClass: "group-border",
		marryChildren: true,
		children: [
			{
				field: "Utilization",
				headerName: "",
				headerClass: "group-border",
				cellStyle: SECTION_BORDER,
				width: 70,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
		],
	},
	{
		headerName: "TPH",
		headerClass: "group-border",
		marryChildren: true,
		children: [
			{
				field: "TPH",
				headerName: "Total",
				headerClass: "group-border",
				cellStyle: SECTION_BORDER,
				width: 70,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
			{
				field: "TPHWoDelay",
				headerName: "No Delay",
				width: 85,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
		],
	},
	{
		headerName: "Delays (hours)",
		headerClass: "group-border",
		marryChildren: true,
		children: [
			{
				field: "DelaysRHF",
				headerName: "RHF",
				headerClass: "group-border",
				cellStyle: SECTION_BORDER,
				width: 65,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
			{
				field: "DelaysMill",
				headerName: "Mill",
				width: 65,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
			{
				field: "DelaysShear",
				headerName: "Shear",
				width: 65,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
			{
				field: "DelaysBundler",
				headerName: "Bundler",
				width: 70,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
			{
				field: "DelaysStacker",
				headerName: "Stacker",
				width: 70,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
			{
				field: "DelaysProductChange",
				headerName: "P Change",
				width: 78,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
		],
	},
	{
		headerName: "Speed",
		headerClass: "group-border",
		marryChildren: true,
		children: [
			{
				field: "FinishSpeedGoal",
				headerName: "Goal",
				headerClass: "group-border",
				cellStyle: SECTION_BORDER,
				width: 70,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
			{
				field: "LineSpeed",
				headerName: "Actual",
				width: 75,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
				cellClass: (p) => belowGoalClass(p, "LineSpeed", "FinishSpeedGoal"),
			},
		],
	},
	{
		headerName: "S1 Gap",
		headerClass: "group-border",
		marryChildren: true,
		children: [
			{
				field: "GapStand1Goal",
				headerName: "Goal",
				headerClass: "group-border",
				cellStyle: SECTION_BORDER,
				width: 65,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
			{
				field: "S1GapGoalPercent",
				headerName: "% Goal",
				width: 70,
				type: "rightAligned",
				// Hide zeros (matches legacy: when no billets fall under the goal,
				// the cell is left empty instead of showing "0").
				valueFormatter: (p) => {
					const v = p.value as number | null;
					if (v == null || v === 0) return "";
					return v.toFixed(2);
				},
			},
		],
	},
	{
		headerName: "S17 Gap",
		headerClass: "group-border",
		marryChildren: true,
		children: [
			{
				field: "GapStand17",
				headerName: "Actual",
				headerClass: "group-border",
				cellStyle: SECTION_BORDER,
				width: 75,
				type: "rightAligned",
				valueFormatter: (p) => fmt2(p.value),
			},
		],
	},
];

const URL_SCHEMA = {
	start: urlParam.date(() => moment().subtract(5, "days")),
	end: urlParam.date(() => moment()),
};

export const RMShiftReport = () => {
	const [{ start, end }, setFilters, { resetValues, isDefault }] =
		useUrlState(URL_SCHEMA);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const { data, isFetching, error } = useRMShiftReport(startStr, endStr);
	const [errorMsg, setErrorMsg] = useState("");

	const columnDefs = useMemo(() => buildColumnDefs(), []);

	if (error) {
		if (!errorMsg) setErrorMsg((error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				RM Shift Report
			</Typography>
			{gridStyles}
			<Paper elevation={3}>
				<ToolbarControls
					onReset={() => resetValues()}
					canReset={!isDefault()}
					start={start}
					end={end}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
				/>
			</Paper>

			<Paper
				elevation={3}
				className="rm-shift-grid"
				sx={{
					flex: 1,
					position: "relative",
					overflow: "auto",
				}}
			>
				{isFetching && !data && (
					<Box
						sx={{
							position: "absolute",
							inset: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 1,
							bgcolor: "rgba(255,255,255,0.7)",
						}}
					>
						<CircularProgress />
					</Box>
				)}
				<AgGridReact<RMShiftReportRow>
					columnDefs={columnDefs}
					rowData={data ?? []}
					defaultColDef={{
						sortable: false,
						resizable: true,
						suppressMovable: true,
						flex: 1,
					}}
					groupHeaderHeight={30}
					headerHeight={30}
					rowHeight={30}
					domLayout="autoHeight"
				/>
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
